// components/dhulHijjahDonation.jsx
import { Builder } from '@builder.io/react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedCurrency } from '../../../../../store/user';
import { selectProducts } from '../../../../../store/products';
import DhulHijjahSummary from '../../../../dhulHijjahDonation/summary';
import { calculateDhulHijjahDonations } from '../../../../../utils/dhulHijjahDonationUtils';
import SelectCurrency from '../../../../general/selectCurrency';
import SectionTitle from '../../../../dhulHijjahDonation/sectionTitle';
import ProductSelector from '../../../../recurringDonation/productSelector';
import TimeZoneSelector from '../../../../recurringDonation/timeZoneSelector';
import 'react-datepicker/dist/react-datepicker.css';
import DateSelector from '../../../../dhulHijjahDonation/dateSelector';
import { DateTime } from 'luxon';
import DonationCalendar from '../../../../dhulHijjahDonation/donationCalendar';
import useHandleAddProduct from '../../../../../utils/handleAddProduct';
import MultiplierSelector from '../../../../dhulHijjahDonation/multiplierSelector';

const DhulHijjahDonationWidget = (props) => {
    const {
        productsList = [],
        processingTime = '11:00'
    } = props;
    const selectedCurrency = useSelector(selectSelectedCurrency);
    const productsFromStore = useSelector(selectProducts);
    const [addProduct] = useHandleAddProduct();

    const [allProducts, setAllProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [timeZone, setTimeZone] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const [donationSchedule, setDonationSchedule] = useState([]);
    const [summary, setSummary] = useState({ totalAmount: 0, totalDays: 0, dailyAmount: 0 });
    const [donationOption, setDonationOption] = useState('equal'); // 'equal' or 'custom'
    const [selectedConditions, setSelectedConditions] = useState([]); // Array of condition values
    const [multipliers, setMultipliers] = useState({
        friday: 2,
        // lastTenNights: 2,
        arrafahDay: 2,
        qurbanDay: 2,
    });
    const [numberOfDays, setNumberOfDays] = useState(10);
    const calendarRef = useRef(null);

    // Determine the selected strategies based on donationOption and selectedConditions.
    const determineSelectedStrategies = () => {
        // console.log("selected strategies==",donationOption)
        if (donationOption === 'equal') {
            return [
                {
                    label: 'Equal Division',
                    multiplier: 1,
                    nights: numberOfDays,
                },
            ];
        } else if (donationOption === 'custom') {
            return selectedConditions.map(condition => {
                let label = '';
                let multiplier = multipliers[condition] || 1;
                let applicableDays = 0;

                switch (condition) {
                    case 'friday':
                        label = 'on Jummah (Friday)';
                        applicableDays = donationSchedule.filter(day => {
                            const date = DateTime.fromISO(day.date, { zone: timeZone });
                            return date.weekday === 5;
                        }).length;
                        break;
                    case 'arrafahDay':
                        label = 'on day of Arafat (9th)';
                        applicableDays = donationSchedule.some(day => day.date === startDate && numberOfDays === 9) ? 1 : 0;
                        break;
                    case 'qurbanDay':
                        label = 'on day of Eid (10th)';
                        applicableDays = donationSchedule.some(day => day.date === startDate && numberOfDays === 10) ? 1 : 0;
                        break;
                    default:
                        label = '';
                        applicableDays = 0;
                }

                return {
                    label,
                    multiplier,
                    nights: applicableDays,
                };
            });
        } else {
            return [];
        }
    };

    const selectedStrategies = determineSelectedStrategies();

    // Load products list
    useEffect(() => {
        // if (!productsFromStore || !Object.keys(productsFromStore).length) return;
        // const temp = [];
        // Object.values(productsFromStore).forEach(category => {
        //     category.sub_categories?.forEach(subCat => {
        //         subCat.products.forEach(p => {
        //             if (Number(p.status) === 1) {
        //                 let creators = Array.isArray(productsList) ? productsList : productsList.split(',').map(s => s.trim());
        //                 if (creators.includes(p.creator)) temp.push(p);
        //             }
        //         });
        //     });
        // });
        // setAllProducts(temp);
        if (productsFromStore && Object.keys(productsFromStore).length) {
            const temp = [];
            const tempDefaultProductsList = [
                'OPR-GEN-SUP-2023-01-01',
                'MD-AA-2024-02',
                'MKD-MN-001',
                'MD-AA-2024-07',
                'MKD-GEN-MZK-2023-01-123'
            ];
            Object.values(productsFromStore).forEach((category) => {
                if (category.sub_categories) {
                    category.sub_categories.forEach((subCat) => {
                        subCat.products.forEach((p) => {
                            // check if this product is in productsList
                            if (Number(p.status) === 1) {
                                if (productsList) {
                                    // Ensure CustomProductsList is always an array
                                    let tempCustomProducts = Array.isArray(productsList)
                                        ? productsList
                                        : productsList.split(',').map(item => item.trim()); // Convert string to array and trim spaces
                                    // Iterate through CustomProductsList to maintain the order
                                    tempCustomProducts.forEach((productCreator, __index) => {
                                        if (p.creator === productCreator) {
                                            temp[__index] = p;
                                        }
                                    });
                                } else {
                                    const matchedProduct = tempDefaultProductsList.find(
                                        (product) => product === p.creator
                                    );
                                    if (matchedProduct) {
                                        temp.push(p);
                                    }
                                }
                            }
                        });
                    });
                }
            });
            setAllProducts(temp);
        }
    }, [productsFromStore, productsList]);

    // Default start date = May 29 or today if later
    useEffect(() => {
        // console.log("start date=",startDate)
        if (startDate instanceof Date) return;

        const now = DateTime.now().setZone(timeZone).startOf('day');
        const dhStart = DateTime.fromObject({ year: now.year, month: 5, day: 29 }, { zone: timeZone });
        setStartDate((now > dhStart ? now : dhStart).toJSDate());
    }, [timeZone, startDate]);

    // Load saved data once, early
    useEffect(() => {
        const saved = localStorage.getItem('DHUL_HIJJAH_WIDGET_DATA');
        if (!saved) return;
        // let tempCreators = ['OPR-GEN-SUP-2023-01-01',
        //     'MD-AA-2024-02',
        //     'MKD-MN-001',
        //     'MD-AA-2024-07',
        //     'MKD-GEN-MZK-2023-01-123'];
        try {
            const parsed = JSON.parse(saved);
            // console.log("start date local=", parsed)
            if (parsed.startDate) setStartDate(new Date(parsed.startDate));
            if (parsed.timeZone) setTimeZone(parsed.timeZone);
            if (parsed.summary) setSummary(parsed.summary);
            if (parsed.donationSchedule) setDonationSchedule(parsed.donationSchedule);
            if (parsed.selectedConditions) setSelectedConditions(parsed.selectedConditions);
            if (parsed.donationOption) setDonationOption(parsed.donationOption);
            if (Array.isArray(parsed.selectedProducts) && parsed.selectedProducts.length) {

                const filteredProducts = parsed.selectedProducts.filter(product =>
                    productsList.includes(product.creator)
                );
                setSelectedProducts(filteredProducts);
                // setSelectedProducts(parsed.selectedProducts);
                // parsed.selectedProducts.forEach(item => {
                //     addProduct({ product: item, currency: selectedCurrency, justAddToLocalStorage: true });
                // });
                filteredProducts.forEach(item => {
                    addProduct({ product: item, currency: selectedCurrency, justAddToLocalStorage: true });
                });
            }
        } catch (e) {
            console.error('Failed to parse DHUL_HIJJAH_WIDGET_DATA', e);
        }
    }, []);

    // Recalculate schedule and summary
    useEffect(() => {
        if (!startDate || !selectedProducts.length) {
            setDonationSchedule([]);
            setSummary({ totalAmount: 0, totalDays: 0, dailyAmount: 0 });
            return;
        }
        const { donationSchedule: sched, summary: sum } = calculateDhulHijjahDonations({
            startDate,
            timeZone,
            selectedProducts,
            currency: selectedCurrency,
            splitOnDays: true,
            processingTime,
            multipliers,
            donationOption,
            selectedConditions,
            numberOfNights: donationSchedule.length
        });
        // console.log("🧪 schedule length =", sched.length, "startDate =", startDate, multipliers);
        setDonationSchedule(sched);
        setSummary(sum);
    }, [startDate, timeZone, selectedProducts, selectedCurrency, processingTime, multipliers]);

    // Save widget data
    useEffect(() => {
        localStorage.setItem(
            'DHUL_HIJJAH_WIDGET_DATA',
            JSON.stringify({
                timeZone,
                selectedProducts,
                summary,
                donationSchedule,
                startDate: startDate ? startDate.toISOString() : null,
                selectedStrategies,
                donationOption,
                multipliers,
                selectedConditions
            })
        );
    }, [timeZone, selectedProducts, summary, donationSchedule, startDate, selectedStrategies, multipliers, donationOption, selectedConditions]);


    return (
        <div className="w-full scrollarea min-h-[600px] font-inter">
            <div className="w-full py-4 flex flex-wrap items-start">
                {/* Left Panel */}
                <div className="basis-full lg:basis-2/3 px-4 md:px-4 flex flex-col gap-4 md:gap-6">
                    <SectionTitle count={1} text="Select the total amount you wish to distribute across the first 10 days of Dhul Hijjah" />
                    {/* Start Date + TimeZone + Currency row */}
                    <div className="flex flex-wrap gap-4">
                        {/* Start Date */}
                        <h2 className="w-full text-stone-600">Select the day you wish to start your donations and the amount you wish for us to divide between the first 10 days of Dhul Hijjah.</h2>
                        <div className="w-[calc(50%-0.5rem)] md:w-auto md:flex-1">
                            <DateSelector
                                startDate={startDate}
                                setStartDate={setStartDate}
                                timeZone={timeZone}
                                label="Start Date"
                                enforceMinToday={true}
                            />
                        </div>

                        {/* Time Zone */}
                        <TimeZoneSelector timeZone={timeZone} setTimeZone={setTimeZone} classNames={'w-[calc(50%-0.5rem)] md:w-auto md:flex-1'} />

                        {/* Currency */}
                        <div className="w-full md:w-auto md:flex-1">
                            <label className="block text-stone-500 mb-1-">Currency</label>
                            <div
                                className="cursor-pointer relative w-full rounded-md h-12 border border-[#282828]"
                                id="ramadan-daily-currency"
                            >
                                <SelectCurrency
                                    onChangeCurrency={() => { }}
                                    className="h-full [&>div]:h-full-"
                                />
                            </div>
                        </div>
                    </div>
                    <SectionTitle count={2} text="Select your giving pattern" />
                    <h2 className="w-full text-stone-600">Below you’ll find options that allow you to further customise your giving during the holy month. For example, you can select to give more by three times on Jummah or more by two times during the first 10 days of Dhul Hijjah.</h2>
                    <MultiplierSelector
                        donationOption={donationOption}
                        setDonationOption={setDonationOption}
                        selectedConditions={selectedConditions}
                        setSelectedConditions={setSelectedConditions}
                        multipliers={multipliers}
                        setMultipliers={setMultipliers}
                    />
                    <SectionTitle count={3} text="Breaking it down" />
                    <h2 className="w-full text-stone-600">Below you’ll find a breakdown of your donations across the first 10 days of Dhul Hijjah. If you wish to divide your amount by project or cause, you can do this also in the designated project boxes shown below!</h2>
                    <ProductSelector
                        products={allProducts}
                        selectedProducts={selectedProducts}
                        setSelectedProducts={setSelectedProducts}
                        currency={selectedCurrency}
                    />
                </div>

                {/* Right Panel */}
                <div className="basis-full lg:basis-1/3 px-4 sticky top-28">
                    <DhulHijjahSummary
                        selectedProducts={selectedProducts}
                        // numberOfNights={donationSchedule.filter((item) => item.shares === 1).length}
                        numberOfNights={donationSchedule.length}
                        startDate={startDate}
                        summary={summary}
                        totalAmount={summary.totalAmount}
                        selectedStrategies={selectedStrategies} // Updated prop
                        currency={selectedCurrency}
                        selectedConditions={selectedConditions}
                        donationOption={donationOption}
                    />
                </div>
            </div>

            {/* Calendar */}
            <div className="w-full mt-6 px-4" ref={calendarRef}>
                <DonationCalendar
                    donationSchedule={donationSchedule}
                    currency={selectedCurrency}
                    startDate={startDate}
                    timeZone={timeZone}
                    totalAmount={summary.totalAmount}
                    originalStartDate={startDate} // pass again for reference
                />
            </div>
        </div>
    );
};

Builder.registerComponent(DhulHijjahDonationWidget, {
    name: 'DhulHijjahDonationWidget',
    inputs: [
        {
            name: 'productsList',
            type: 'string',
        },
        {
            name: 'processingTime',
            type: 'string'
        }
    ]
});

export default DhulHijjahDonationWidget;